import { OverlayTooltip } from '../../../core/components/overlay-tooltip/OverlayTooltip.js';
import { useSelector as useOverlayTooltipSelector } from '../../../core/components/overlay-tooltip/selectors.js';
import { isTooltipDataEmpty } from '../../../core/components/overlay-tooltip/utils.js';
import { useTooltipSeriesActions } from '../../../core/components/tooltip/shared/hooks/useTooltipSeriesActions.js';
import type { SeriesActionsTemplate } from '../../../core/types/series-actions-template.js';
import { type TooltipItem } from '../../../core/types/tooltip.js';
import { useVisibleSlicesState } from '../../hooks/store/selectors.js';
import { usePieChartTooltip } from '../../hooks/usePieChartTooltip.js';
import type { InternalSliceData } from '../../types/internal/pie-chart.js';
import type { PieChartTooltipPayload } from '../../types/pie-chart.config.js';

export const PieChartTooltip = () => {
  const customTooltip = usePieChartTooltip();
  const overlayTooltipPinned = useOverlayTooltipSelector(
    (state) => state.pinned,
  );
  const overlayData = useOverlayTooltipSelector((state) => state.data);
  const visibleSlices = useVisibleSlicesState();

  const seriesActions = useTooltipSeriesActions<InternalSliceData>(
    visibleSlices,
    (slice) => slice.id,
  );

  if (customTooltip?.hidden || isTooltipDataEmpty(overlayData)) {
    return null;
  }

  const metadata = overlayData.metadata as { sliceId?: string } | undefined;
  const currentSeries = overlayData.series[0];
  const fallbackSeriesId =
    typeof currentSeries.name === 'string' ? currentSeries.name : '';

  if (customTooltip?.template) {
    const payload: PieChartTooltipPayload = {
      category:
        typeof currentSeries.name === 'string' ? currentSeries.name : '',
      value: currentSeries.value as string | number,
      percentage: currentSeries.relativeValue as number,
      color: currentSeries.color,
    };

    return <OverlayTooltip legacyTemplate={customTooltip.template(payload)} />;
  }

  const tooltipItem: TooltipItem | undefined = currentSeries
    ? {
        id: metadata?.sliceId ?? fallbackSeriesId,
        name: currentSeries.name,
        color: { Default: currentSeries.color },
        values: [
          currentSeries.value?.toString() || '',
          currentSeries.relativeValue?.toString() || '',
        ] as string[],
      }
    : undefined;

  const actions = (
    overlayTooltipPinned && tooltipItem
      ? () => seriesActions(tooltipItem)
      : undefined
  ) as (() => SeriesActionsTemplate) | undefined;

  return (
    <OverlayTooltip>
      <OverlayTooltip.SeriesItem
        name={currentSeries.name}
        value={currentSeries.value as string | number}
        color={currentSeries.color}
        relativeValue={currentSeries.relativeValue}
        relativeValueUnit={currentSeries.relativeValueUnit}
        actions={actions}
      />
    </OverlayTooltip>
  );
};
